import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiErrorDetail, authFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileDown, Loader2, FileText, Trash2, Sparkles, Send } from "lucide-react";
import { ShareSheet } from "@/components/ShareSheet";

async function downloadReport(id, filename) {
  const res = await authFetch(`${API}/reports/${id}/download`);
  if (!res.ok) throw new Error("Téléchargement impossible");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "rapport-respondia.pdf"; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function ReportsCard({ establishment }) {
  const [share, setShare] = useState(null);
  const [reports, setReports] = useState([]);
  const [withAi, setWithAi] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/establishments/${establishment.id}/reports`);
    setReports(data);
  }, [establishment.id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/establishments/${establishment.id}/reports`, { include_ai_summary: withAi });
      toast.success("Rapport PDF généré");
      await load();
      await downloadReport(data.id, data.filename);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/reports/${id}`); load(); };

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="reports-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-slate-700" /> Rapport PDF</h2>
          <p className="text-xs text-slate-500 mt-0.5">Rapport professionnel généré à partir de vos données réellement mesurées.</p>
        </div>
        <Button onClick={generate} disabled={busy} data-testid="download-report-button" className="bg-slate-900 hover:bg-slate-800 rounded-xl">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          {busy ? (withAi ? "Génération (synthèse IA)…" : "Génération…") : "Télécharger mon rapport PDF"}
        </Button>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer w-fit">
        <Checkbox checked={withAi} onCheckedChange={(v) => setWithAi(!!v)} data-testid="report-ai-summary-checkbox" />
        <Sparkles className="w-3.5 h-3.5 text-indigo-500" /> Inclure une synthèse rédigée par Lia (≈ 20 s de plus)
      </label>
      {reports.length > 0 && (
        <div className="mt-4 divide-y divide-slate-100" data-testid="reports-history">
          {reports.slice(0, 6).map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`report-row-${r.id}`}>
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-slate-800">{r.filename}</div>
                <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString("fr-FR")} · score {r.global_score ?? "non mesuré"} · {Math.round(r.size / 1024)} Ko{r.ai_summary_generated ? " · synthèse IA" : ""}{r.kind === "monthly" ? " · RAPPORT MENSUEL" : ""}{r.is_demo ? " · DÉMO" : ""}</div>
              </div>
              <button onClick={() => downloadReport(r.id, r.filename).catch(() => toast.error("Téléchargement impossible"))} className="text-blue-600 hover:text-blue-800 text-xs font-medium" data-testid={`report-download-${r.id}`}>Télécharger</button>
              <button onClick={() => setShare(r)} className="text-slate-600 hover:text-slate-900 text-xs font-medium inline-flex items-center gap-1" data-testid={`report-share-${r.id}`}><Send className="w-3.5 h-3.5" />Partager</button>
              <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`report-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {share && <ShareSheet estId={establishment.id} contentType="report" contentId={share.id} title={share.filename} onClose={() => setShare(null)} />}
    </div>
  );
}
